import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search, Flame, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

export default function TopModelsGrid() {
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/catalog/top-models", { params: { limit: 8 } })
      .then((r) => { if (alive) setItems(r.data || []); })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (!loading && items.length === 0) return null;

  const kicker = lang === "en" ? "Hot picks" : "Modelli caldi";
  const title = lang === "en" ? "Most searched models" : "Modelli più cercati";
  const seeAll = lang === "en" ? "See all catalog" : "Vedi tutto il catalogo";
  const fromLabel = lang === "en" ? "from" : "da";

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-20" data-testid="top-models-section">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="font-mono-tech text-xs uppercase tracking-widest text-xd-orange inline-flex items-center gap-1.5">
            <Flame className="w-3.5 h-3.5" /> {kicker}
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-xd-text mt-1 tracking-tight">
            {title}
          </h2>
          <p className="text-sm text-xd-muted mt-2 max-w-md">
            {lang === "en"
              ? "Jump straight to the head unit that fits your model — chosen by our customers."
              : "Vai direttamente all'autoradio giusta per il tuo modello — la scelta dei nostri clienti."}
          </p>
        </div>
        <Link
          to="/catalogo"
          data-testid="top-models-see-all"
          className="text-sm font-medium text-xd-cyanHover hover:underline hidden sm:inline-flex items-center gap-1"
        >
          {seeAll} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {loading
          ? Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="rounded-xl bg-white border border-xd-border overflow-hidden">
                <div className="aspect-[4/3] bg-slate-100 animate-pulse" />
                <div className="p-4 space-y-2">
                  <div className="h-3 w-1/3 bg-slate-200 rounded animate-pulse" />
                  <div className="h-4 w-2/3 bg-slate-200 rounded animate-pulse" />
                  <div className="h-3 w-1/2 bg-slate-200 rounded animate-pulse" />
                </div>
              </div>
            ))
          : items.map((it) => (
              <TopModelCard key={`${it.brand}-${it.model}`} it={it} lang={lang} fromLabel={fromLabel} />
            ))}
      </div>
    </section>
  );
}

function TopModelCard({ it, lang, fromLabel }) {
  const images = (it.images && it.images.length ? it.images : (it.image ? [it.image] : [])).slice(0, 3);
  const [idx, setIdx] = useState(0);
  const hasMultiple = images.length > 1;

  useEffect(() => {
    if (!hasMultiple) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % images.length), 3500);
    return () => clearInterval(id);
  }, [hasMultiple, images.length]);

  const nav = (dir, e) => {
    e.preventDefault();
    e.stopPropagation();
    setIdx((i) => (i + dir + images.length) % images.length);
  };

  return (
    <Link
      to={`/catalogo?brand=${encodeURIComponent(it.brand)}&model=${encodeURIComponent(it.model)}`}
      data-testid={`top-model-${it.brand}-${it.model}`}
      className="group rounded-xl bg-white border border-xd-border overflow-hidden hover:border-xd-cyan hover:shadow-lg transition-all"
    >
      <div className="relative aspect-[4/3] bg-slate-50 overflow-hidden">
        {images.length > 0 ? (
          images.map((src, i) => (
            <img
              key={src + i}
              src={src}
              alt={`${it.brand} ${it.model}`}
              loading="lazy"
              data-testid={`top-model-image-${i}`}
              className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 ${
                i === idx ? "opacity-100 scale-100" : "opacity-0 scale-105"
              } group-hover:scale-[1.03]`}
            />
          ))
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300">
            <Search className="w-8 h-8" />
          </div>
        )}

        {hasMultiple && (
          <>
            <button
              type="button"
              onClick={(e) => nav(-1, e)}
              data-testid="top-model-carousel-prev"
              aria-label={lang === "en" ? "Previous image" : "Immagine precedente"}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 backdrop-blur-sm text-xd-text opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity flex items-center justify-center shadow-md hover:bg-white"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={(e) => nav(1, e)}
              data-testid="top-model-carousel-next"
              aria-label={lang === "en" ? "Next image" : "Immagine successiva"}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 backdrop-blur-sm text-xd-text opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity flex items-center justify-center shadow-md hover:bg-white"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
              {images.map((_, i) => (
                <span
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${i === idx ? "bg-xd-cyan" : "bg-white/60"}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
      <div className="p-4">
        <div className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-cyanHover">{it.brand}</div>
        <div className="font-display text-lg font-semibold text-xd-text mt-0.5 line-clamp-1">{it.model}</div>
        <div className="mt-2 flex items-center justify-between text-xs text-xd-muted">
          <span className="inline-flex items-center gap-1">
            <span className="font-medium text-xd-text">{it.count}</span>
            {lang === "en" ? "products" : "prodotti"}
          </span>
          {typeof it.min_price === "number" && it.min_price > 0 && (
            <span className="font-mono-tech text-xd-orange">
              {fromLabel} € {Math.round(it.min_price)}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
